/**
 * Invoice Generator Utility
 * Generates invoice data from cart items and calculates all totals
 */

/**
 * Generate a unique invoice number
 * Format: INV-YYYYMMDD-XXXXX
 */
export const generateInvoiceNumber = () => {
  const date = new Date();
  const dateStr = date.toISOString().slice(0, 10).replace(/-/g, '');
  const random = Math.floor(Math.random() * 90000) + 10000;
  return `INV-${dateStr}-${random}`;
};

/**
 * Calculate shipping cost based on subtotal
 * Free shipping above ₹5000
 */
export const calculateShipping = (subtotal, freeShippingThreshold = 5000) => {
  return subtotal >= freeShippingThreshold ? 0 : 100;
};

/**
 * Calculate tax (GST 18% in India)
 */
export const calculateTax = (subtotal, taxRate = 0.18) => {
  return subtotal * taxRate;
};

/**
 * Generate complete invoice data from cart items
 * @param {Array} cartItems - Array of cart items
 * @param {Object} options - Optional configuration
 * @returns {Object} Complete invoice data
 */
export const generateInvoiceData = (cartItems, options = {}) => {
  // Validate input
  if (!cartItems || cartItems.length === 0) {
    throw new Error('Cannot generate invoice: Cart is empty');
  }

  const {
    customerInfo = null,
    discount = 0,
    includeTax = false,
    couponCode = null,
    notes = ''
  } = options;

  // Calculate item totals
  const items = cartItems.map(item => {
    const price = item.discount_price || item.price;
    const originalPrice = item.price;
    const total = price * item.quantity;
    const itemSavings = (originalPrice - price) * item.quantity;

    return {
      id: item.id,
      name: item.name,
      category: item.category || '',
      brand: item.brand || '',
      quantity: item.quantity,
      price: price,
      originalPrice: originalPrice,
      total: total,
      savings: itemSavings
    };
  });

  // Calculate subtotal
  const subtotal = items.reduce((sum, item) => sum + item.total, 0);

  // Calculate total savings from discounted items
  const savings = items.reduce((sum, item) => sum + item.savings, 0);

  // Calculate shipping
  const shipping = calculateShipping(subtotal);

  // Calculate tax if needed
  const tax = includeTax ? calculateTax(subtotal) : 0;

  // Calculate final total
  const total = subtotal + shipping + tax - discount;

  // Generate invoice
  const invoiceData = {
    invoiceNumber: generateInvoiceNumber(),
    invoiceDate: new Date().toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }),
    generatedAt: new Date().toISOString(),
    customerInfo: customerInfo || {
      name: 'Walk-in Customer',
      email: '',
      phone: '',
      address: ''
    },
    items: items,
    subtotal: subtotal,
    discount: discount,
    couponCode: couponCode,
    shipping: shipping,
    tax: tax,
    savings: savings,
    total: total,
    itemCount: items.length,
    totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
    notes: notes,
    paymentStatus: 'Pending',
    deliveryStatus: 'Processing'
  };

  return invoiceData;
};

/**
 * Format invoice data for WhatsApp message
 * Creates a professionally formatted text message with invoice details
 */
export const formatInvoiceForWhatsApp = (invoiceData) => {
  if (!invoiceData) {
    throw new Error('Invoice data is required');
  }

  const {
    invoiceNumber,
    invoiceDate,
    customerInfo,
    items,
    subtotal,
    shipping,
    discount,
    tax,
    total,
    savings,
    couponCode
  } = invoiceData;

  // Build WhatsApp message with better formatting
  let message = `================================\n`;
  message += `       *TEETHKART*\n`;
  message += `  India's #1 Dental Store\n`;
  message += `================================\n\n`;
  
  message += `*ORDER INVOICE*\n`;
  message += `${'-'.repeat(35)}\n`;
  message += `Invoice No: *${invoiceNumber}*\n`;
  message += `Date: ${invoiceDate}\n`;
  message += `Time: ${new Date().toLocaleTimeString('en-IN', {hour: '2-digit', minute: '2-digit'})}\n`;
  message += `${'-'.repeat(35)}\n\n`;

  // Customer info with better formatting
  if (customerInfo && customerInfo.name && customerInfo.name !== 'Walk-in Customer') {
    message += `*CUSTOMER DETAILS*\n`;
    message += `${'-'.repeat(35)}\n`;
    message += `Name: *${customerInfo.name}*\n`;
    if (customerInfo.phone) message += `Phone: ${customerInfo.phone}\n`;
    if (customerInfo.email) message += `Email: ${customerInfo.email}\n`;
    if (customerInfo.address) message += `Address: ${customerInfo.address}\n`;
    message += `${'-'.repeat(35)}\n\n`;
  }

  // Items with table-like formatting
  message += `*ORDER ITEMS*\n`;
  message += `${'-'.repeat(35)}\n`;
  
  items.forEach((item, index) => {
    message += `\n*${index + 1}. ${item.name}*\n`;
    if (item.brand) message += `   Brand: ${item.brand}\n`;
    if (item.category) message += `   Category: ${item.category}\n`;
    message += `   Quantity: ${item.quantity} unit${item.quantity > 1 ? 's' : ''}\n`;
    message += `   Price: ₹${item.price.toLocaleString('en-IN')} each\n`;
    
    // Show savings if item was discounted
    if (item.originalPrice && item.originalPrice > item.price) {
      const itemSaving = (item.originalPrice - item.price) * item.quantity;
      message += `   (Saved: Rs.${itemSaving.toLocaleString('en-IN')})\n`;
    }
    
    message += `   *Subtotal: Rs.${item.total.toLocaleString('en-IN')}*\n`;
  });
  
  message += `\n${'-'.repeat(35)}\n\n`;

  // Pricing Summary with better alignment
  message += `*PAYMENT SUMMARY*\n`;
  message += `${'-'.repeat(35)}\n`;
  message += `Subtotal:${' '.repeat(18)}Rs.${subtotal.toLocaleString('en-IN')}\n`;
  
  if (savings > 0) {
    message += `Product Savings:${' '.repeat(9)}-Rs.${savings.toLocaleString('en-IN')}\n`;
  }
  
  if (discount > 0) {
    const discountLabel = couponCode ? `Coupon (${couponCode})` : 'Discount';
    message += `${discountLabel}:${' '.repeat(Math.max(0, 19 - discountLabel.length))}-Rs.${discount.toLocaleString('en-IN')}\n`;
  }
  
  if (shipping > 0) {
    message += `Shipping:${' '.repeat(17)}Rs.${shipping.toLocaleString('en-IN')}\n`;
  } else {
    message += `Shipping:${' '.repeat(17)}*FREE*\n`;
  }
  
  if (tax > 0) {
    message += `Tax (GST 18%):${' '.repeat(11)}Rs.${tax.toLocaleString('en-IN')}\n`;
  }
  
  message += `${'='.repeat(35)}\n`;
  message += `*TOTAL AMOUNT:${' '.repeat(12)}Rs.${total.toLocaleString('en-IN')}*\n`;
  message += `${'='.repeat(35)}\n\n`;

  // Additional savings highlight
  const totalSavings = savings + discount;
  if (totalSavings > 0) {
    message += `*You Saved Rs.${totalSavings.toLocaleString('en-IN')} on this order!*\n\n`;
  }

  // Order confirmation and next steps
  message += `*ORDER CONFIRMED*\n\n`;
  message += `*Next Steps:*\n`;
  message += `1. Our team will verify your order\n`;
  message += `2. We'll contact you for delivery details\n`;
  message += `3. Expect delivery within 3-5 business days\n\n`;
  
  message += `${'-'.repeat(35)}\n`;
  message += `*Need Help?*\n`;
  message += `Call: +91-8999-250-629\n`;
  message += `Email: Omprahalladbhandary@gmail.com\n`;
  message += `Website : www.teethkart.in\n`;
  message += `${'-'.repeat(35)}\n\n`;
  
  message += `*Thank you for choosing TeethKart!*\n`;
  message += `India's Most Trusted Dental Store\n\n`;

  return message;
};

/**
 * Generate WhatsApp URL with pre-filled message
 * @param {Object} invoiceData - Invoice data object
 * @param {String} phoneNumber - Business WhatsApp number (default: +918999250629)
 * @returns {String} WhatsApp URL
 */
export const generateWhatsAppURL = (invoiceData, phoneNumber = '918999250629') => {
  const message = formatInvoiceForWhatsApp(invoiceData);
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
};

/**
 * Validate cart items before invoice generation
 * @param {Array} cartItems - Cart items to validate
 * @returns {Object} Validation result
 */
export const validateCartForInvoice = (cartItems) => {
  const errors = [];
  
  if (!cartItems || cartItems.length === 0) {
    errors.push('Cart is empty');
  }
  
  if (cartItems) {
    cartItems.forEach((item, index) => {
      if (!item.name) {
        errors.push(`Item ${index + 1}: Missing product name`);
      }
      if (!item.price && !item.discount_price) {
        errors.push(`Item ${index + 1}: Missing price`);
      }
      if (!item.quantity || item.quantity <= 0) {
        errors.push(`Item ${index + 1}: Invalid quantity`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors: errors
  };
};

/**
 * Export invoice data as JSON
 * @param {Object} invoiceData - Invoice data
 * @returns {String} JSON string
 */
export const exportInvoiceJSON = (invoiceData) => {
  return JSON.stringify(invoiceData, null, 2);
};

/**
 * Calculate discount from coupon code
 * @param {String} couponCode - Coupon code to validate
 * @param {Number} subtotal - Cart subtotal
 * @returns {Number} Discount amount
 */
export const applyCoupon = (couponCode, subtotal) => {
  const coupons = {
    'FIRST100': 0.10,   // 10% off
    'DIWALI15': 0.15    // 15% off
  };

  const coupon = coupons[couponCode.toUpperCase()];
  
  if (!coupon) {
    return 0;
  }

  // If coupon value is < 1, it's a percentage
  if (coupon < 1) {
    return subtotal * coupon;
  }
  
  // Otherwise it's a flat amount
  return coupon;
};


